import React, { useMemo } from 'react';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { Point } from '@visx/point';
import { Line, LineRadial } from '@visx/shape';

const graphFill = '#3BC75E';
const graphStroke = '#2EB450';
const lineColor = '#9BB3C5';
const background = 'transparent';

const LEVEL_FILL = { 0: '#E53935', 1: '#6B8E23', 2: '#F99407', 3: '#2EB450' };

const CHART_SIZE = 400;
const BASE_MARGIN = 72;
/** 레이더 방사형 골격 최소 축 수 (2개 이하 대분류도 육각/사각형 웹 표시) */
const MIN_VISUAL_AXES = 4;

const EMPTY_AXIS_ITEM = {
  value: 0,
  rawValue: 0,
  level: 0,
  label: '',
  hidden: true,
};

const y = (d) => Number(d.frequency ?? d.value ?? 0);

/** i번째 축 각도(라디안) — 0번 축이 12시 방향 */
const axisAngleRad = (index, count) =>
  (index * 2 * Math.PI) / count - Math.PI / 2;

/** 중대재해 처벌법 레이더 — 데이터 없을 때 기본 골격 */
export const SERIOUS_RADAR_EMPTY_DATA = [
  { label: '경영자 안전보건경영활동', subject: '경영자 안전보건경영활동(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
  { label: '위험성 평가', subject: '위험성 평가(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
  { label: '안전보건교육', subject: '안전보건교육(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
  { label: '근로자 참여', subject: '근로자 참여(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
  { label: '중대재해 대응조치', subject: '중대재해 대응조치(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
  { label: '도급.용역.위탁 안전보건확보', subject: '도급.용역.위탁 안전보건확보(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
  { label: '평가 및 개선', subject: '평가 및 개선(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
  { label: '안전문화 및 안전의식', subject: '안전문화 및 안전의식(0)', value: 0, fullMark: 100, level: 0, rawValue: 0 },
];

const genWebAngles = (count) =>
  [...new Array(count + 1)].map((_, i) => ({
    angle: axisAngleRad(i % count, count),
  }));

const genAxisPoints = (count, radius) =>
  [...new Array(count)].map((_, i) => {
    const angle = axisAngleRad(i, count);
    return { x: radius * Math.cos(angle), y: radius * Math.sin(angle), angle };
  });

function genPolygonPoints(dataArray, scale, getValue) {
  const count = dataArray.length;
  const pointString = dataArray.reduce((res, d, i) => {
    const value = getValue(d);
    const angle = axisAngleRad(i, count);
    const xVal = scale(value) * Math.cos(angle);
    const yVal = scale(value) * Math.sin(angle);
    return `${res + xVal},${yVal} `;
  }, '');
  return { pointString };
}

const resolveItemLabel = (item) => {
  if (item?.label) return String(item.label).trim();
  const subject = String(item?.subject || '').trim();
  const match = subject.match(/^(.*)\((\d+(?:\.\d+)?)\)\s*$/);
  return match ? match[1].trim() : subject;
};

const resolveItemScore = (item) => {
  if (item?.rawValue != null && item.rawValue !== '') {
    return String(item.rawValue);
  }
  const subject = String(item?.subject || '').trim();
  const match = subject.match(/^(.*)\((\d+(?:\.\d+)?)\)\s*$/);
  return match ? match[2] : '';
};

/** 긴 대분류명은 말줄임 (레이더 라벨 겹침 방지) */
const shortenLabel = (text, count) => {
  const maxLen = count > 10 ? 7 : count > 8 ? 8 : 10;
  const t = String(text || '').trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
};

/** 실제 대분류 수가 적을 때 방사형 골격용 축에 데이터 배치 */
const buildVisualRadarData = (data = []) => {
  const sourceCount = data.length;
  const visualCount = Math.max(sourceCount, MIN_VISUAL_AXES);
  const items = Array.from({ length: visualCount }, () => ({
    ...EMPTY_AXIS_ITEM,
  }));

  data.forEach((item, index) => {
    let axisIdx =
      sourceCount >= visualCount
        ? index
        : Math.round((index * visualCount) / sourceCount) % visualCount;

    let guard = 0;
    while (!items[axisIdx]?.hidden && guard < visualCount) {
      axisIdx = (axisIdx + 1) % visualCount;
      guard += 1;
    }

    items[axisIdx] = { ...item, hidden: false };
  });

  return { items, visualCount, sourceCount };
};

const getPlotScale = (visualCount) => {
  if (visualCount <= 8) return 1;
  if (visualCount <= 10) return 0.9;
  if (visualCount <= 12) return 0.82;
  return 0.74;
};

const getLabelStyle = (angle) => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  if (Math.abs(cos) < 0.2) {
    return {
      textAnchor: 'middle',
      dominantBaseline: sin < 0 ? 'auto' : 'hanging',
      dx: 0,
      dy: sin < 0 ? -6 : 6,
    };
  }

  if (cos > 0) {
    return {
      textAnchor: 'start',
      dominantBaseline: 'middle',
      dx: 8,
      dy: 0,
    };
  }

  return {
    textAnchor: 'end',
    dominantBaseline: 'middle',
    dx: -8,
    dy: 0,
  };
};

export default function RadarChart({
  data = [],
  levels = 4,
  showEmptySkeleton = true,
}) {
  const isEmptyPlaceholder = !data?.length;
  const sourceData = !isEmptyPlaceholder
    ? data
    : showEmptySkeleton
      ? SERIOUS_RADAR_EMPTY_DATA
      : [];

  const { items: plotData, visualCount, sourceCount } = useMemo(
    () => buildVisualRadarData(sourceData),
    [sourceData]
  );

  const layout = useMemo(() => {
    const plotScale = getPlotScale(visualCount);
    const inner = CHART_SIZE - BASE_MARGIN * 2;
    const radius = (inner / 2) * plotScale;
    const labelR = radius + 22;
    const fontSize =
      sourceCount > 10 ? 10 : sourceCount > 8 ? 11 : 12;
    return { radius, labelR, fontSize, plotScale };
  }, [visualCount, sourceCount]);

  if (sourceCount === 0) {
    return (
      <svg
        viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }

  const { radius, labelR, fontSize } = layout;
  const center = CHART_SIZE / 2;

  const yScale = scaleLinear({
    range: [0, radius],
    domain: [0, 100],
  });

  const webs = genWebAngles(visualCount);
  const axisPoints = genAxisPoints(visualCount, radius);
  const polygonPoints = genPolygonPoints(
    plotData,
    (d) => yScale(d) ?? 0,
    y
  );
  const zeroPoint = new Point({ x: 0, y: 0 });

  return (
    <svg
      viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
      className="w-full h-full block"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="중대재해처벌법 이행 레이더 차트"
    >
      <rect fill={background} width={CHART_SIZE} height={CHART_SIZE} rx={14} />
      <Group top={center} left={center}>
        {[...new Array(levels)].map((_, i) => (
          <LineRadial
            key={`web-${i}`}
            data={webs}
            angle={(d) => d.angle}
            radius={((i + 1) * radius) / levels}
            fill="none"
            stroke={lineColor}
            strokeWidth={1}
            strokeOpacity={0.8}
          />
        ))}
        {axisPoints.map((point, i) => (
          <Line
            key={`radar-line-${i}`}
            from={zeroPoint}
            to={point}
            stroke={lineColor}
          />
        ))}
        <polygon
          points={polygonPoints.pointString}
          fill={graphFill}
          fillOpacity={isEmptyPlaceholder ? 0.2 : 0.47}
          stroke={graphStroke}
          strokeWidth={1}
        />
        {axisPoints.map((point, i) => {
          const item = plotData[i];
          if (item?.hidden) return null;

          const fullLabel = resolveItemLabel(item);
          const shortLabel = shortenLabel(fullLabel, sourceCount);
          const score = resolveItemScore(item);
          const level = Number(item?.level) || 0;
          const lx = labelR * Math.cos(point.angle);
          const ly = labelR * Math.sin(point.angle);
          const labelStyle = getLabelStyle(point.angle);
          const scoreColor = LEVEL_FILL[level] ?? LEVEL_FILL[1];

          return (
            <text
              key={`tick-${i}-${fullLabel}`}
              x={lx + labelStyle.dx}
              y={ly + labelStyle.dy}
              textAnchor={labelStyle.textAnchor}
              dominantBaseline={labelStyle.dominantBaseline}
              fontSize={fontSize}
            >
              <title>{`${fullLabel} ${score}점`}</title>
              <tspan fill="#555" fontWeight={500}>
                {shortLabel}{' '}
              </tspan>
              <tspan fill={scoreColor} fontWeight={700}>
                ({score})
              </tspan>
            </text>
          );
        })}
      </Group>
    </svg>
  );
}
